import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { normalizeQueueStatusReference, normalizeQueueStatusReport } from '../model/queue-status.model';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const apiBase = API_ROUTES.qstat.queueReports;
function buildParams(params) {
    const q = new URLSearchParams();
    if (params.startDate)
        q.set('startDate', params.startDate);
    if (params.endDate)
        q.set('endDate', params.endDate);
    if (params.queues?.length)
        q.set('queues', params.queues.join(','));
    if (params.agents?.length)
        q.set('agents', params.agents.join(','));
    return q;
}
const initialState = {
    loading: false,
    referenceLoading: false,
    detailsLoading: false,
    downloading: false,
    errorMessage: null,
    reference: null,
    report: null,
    answeredDetails: [],
    unansweredDetails: [],
};
export const getQueueStatusReference = createAsyncThunk('queueStatus/fetch_reference', async () => {
    const [dashboardRes, phoneUsersRes] = await Promise.all([
        axios.get(API_ROUTES.qstat.dashboard),
        axios.get(API_ROUTES.qstat.phoneUsers).catch(() => ({ data: null })),
    ]);
    return normalizeQueueStatusReference(dashboardRes.data, phoneUsersRes.data);
}, { serializeError: serializeAxiosError });
export const getQueueStatusReport = createAsyncThunk('queueStatus/fetch_report', async (params) => {
    const q = buildParams(params);
    const res = await axios.get(`${apiBase}?${q.toString()}`);
    return normalizeQueueStatusReport(res.data);
}, { serializeError: serializeAxiosError });
export const getAnsweredCallDetails = createAsyncThunk('queueStatus/fetch_answered_details', async (params) => {
    const q = buildParams(params);
    q.set('action', params.action);
    if (params.agent)
        q.set('agent', params.agent);
    if (params.queue)
        q.set('queue', params.queue);
    return axios.get(`${apiBase}/answered-details?${q.toString()}`);
}, { serializeError: serializeAxiosError });
export const getUnansweredCallDetails = createAsyncThunk('queueStatus/fetch_unanswered_details', async (params) => {
    const q = buildParams(params);
    q.set('action', params.action);
    if (params.cause)
        q.set('cause', params.cause);
    if (params.queue)
        q.set('queue', params.queue);
    return axios.get(`${apiBase}/unanswered-details?${q.toString()}`);
}, { serializeError: serializeAxiosError });
export const downloadQueueStatusRecording = createAsyncThunk('queueStatus/download', async (fileName) => {
    const q = new URLSearchParams({ fileName });
    return axios.get(`${apiBase}/download?${q.toString()}`, { responseType: 'blob' });
}, { serializeError: serializeAxiosError });
export const queueStatusSlice = createSlice({
    name: 'queueStatus',
    initialState: initialState,
    reducers: {
        clearQueueStatusReport(state) {
            state.report = null;
            state.errorMessage = null;
        },
        clearQueueStatusDetails(state) {
            state.answeredDetails = [];
            state.unansweredDetails = [];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getQueueStatusReference.fulfilled, (state, action) => {
            state.referenceLoading = false;
            state.reference = action.payload;
        })
            .addCase(getQueueStatusReport.fulfilled, (state, action) => {
            state.loading = false;
            state.report = action.payload;
        })
            .addCase(getAnsweredCallDetails.fulfilled, (state, action) => {
            state.detailsLoading = false;
            state.answeredDetails = action.payload.data;
        })
            .addCase(getUnansweredCallDetails.fulfilled, (state, action) => {
            state.detailsLoading = false;
            state.unansweredDetails = action.payload.data;
        })
            .addCase(downloadQueueStatusRecording.pending, (state) => {
            state.downloading = true;
        })
            .addCase(downloadQueueStatusRecording.fulfilled, (state) => {
            state.downloading = false;
        })
            .addCase(downloadQueueStatusRecording.rejected, (state) => {
            state.downloading = false;
        })
            .addMatcher(isPending(getQueueStatusReference), (state) => {
            state.referenceLoading = true;
        })
            .addMatcher(isPending(getQueueStatusReport), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getAnsweredCallDetails, getUnansweredCallDetails), (state) => {
            state.detailsLoading = true;
        })
            .addMatcher(isRejected(getQueueStatusReference, getQueueStatusReport), (state, action) => {
            state.referenceLoading = false;
            state.loading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getAnsweredCallDetails, getUnansweredCallDetails), (state) => {
            state.detailsLoading = false;
        });
    },
});
export const { clearQueueStatusReport, clearQueueStatusDetails } = queueStatusSlice.actions;
export default queueStatusSlice.reducer;
